import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Play, 
  Sparkles, 
  Volume2, 
  VolumeX, 
  Clapperboard,
  Search,
  Film,
  Headphones,
  Zap
} from 'lucide-react';
import liveVideoService from '../../../services/liveVideoService';
import originalsService from '../../../services/originalsService';
import podcastService from '../../../services/podcastService';
import mediaService from '../../../services/mediaService';
import { useNavigate } from 'react-router-dom';
import { useVideoSchema } from '../../../hooks/useVideoSchema';
import VideoSEO from '../../../components/SEO/VideoSEO';
import unifiedAuth from '../../../utils/unifiedAuth';
import { useHlsPlayback } from '../../../hooks/useHlsPlayback';

interface LiveVideoData {
  id: string;
  slug?: string;
  title?: string;
  live_status?: string;
  video_file?: string;
  secure_video_url?: string;
  delivery_format?: 'HLS' | 'PROGRESSIVE';
  secure_thumbnail_url?: string;
}

interface PlatformStats {
  originals: number;
  contents: number; // combined cinema + content
  podcasts: number;
}

// Utility function to format count display
const formatCount = (count: number): string => {
  if (count >= 1000) {
    const thousands = count / 1000;
    return thousands % 1 === 0 ? `${thousands.toFixed(0)}K+` : `${thousands.toFixed(1)}K+`;
  }
  return `${count}+`;
};

const Hero = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [currentLiveVideo, setCurrentLiveVideo] = useState<LiveVideoData | null>(null);

  /**
   * The hero used a bare <video src={...}>, which is why it stopped playing.
   *
   * Safari plays HLS natively; Chrome and Firefox do not. Every other player on the site
   * goes through useHlsPlayback, and the hero never did - so once the endpoint started
   * returning the Bunny Stream manifest it actually should have been playing all along,
   * a plain src would have loaded nothing outside Safari, and reported no error anybody
   * could act on.
   *
   * Declared above the early returns below, because hooks have to run in the same order
   * on every render.
   */
  const heroVideoRef = useRef<HTMLVideoElement | null>(null);
  const [hasTriedFetch, setHasTriedFetch] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Platform stats state
  const [stats, setStats] = useState<PlatformStats>({
    originals: 0,
    contents: 0, // combined cinema + content
    podcasts: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Live video controls
  const [isMuted, setIsMuted] = useState(true);
  // View tracking state. A view is still recorded - the hero no longer *displays* a
  // count, which is a separate question from whether watches are counted at all.
  const [hasRecordedView, setHasRecordedView] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  
  // Homepage URL for schema
  const pageUrl = 'https://strymhub.com/';

  // Video schema data
  const videoSchemaData = currentLiveVideo ? {
    name: currentLiveVideo.title || 'Live Video',
    description: 'Live video streaming on StrymHub',
    thumbnailUrl: currentLiveVideo.secure_thumbnail_url || 'https://strymhub.com/android-chrome-512x512.png',
    uploadDate: new Date().toISOString(),
    contentUrl: currentLiveVideo.secure_video_url || currentLiveVideo.video_file || '',
    embedUrl: `https://strymhub.com/live/${currentLiveVideo.slug}/embed`,
    author: {
      name: 'StrymHub Creator'
    },
    publisher: {
      name: 'StrymHub',
      logo: 'https://strymhub.com/android-chrome-512x512.png'
    }
  } : null;

  // Use video schema hook
  useVideoSchema(videoSchemaData, pageUrl);

  const heroPlaybackUrl = currentLiveVideo?.secure_video_url || currentLiveVideo?.video_file;

  // No onFatalError: a hero that cannot play should fall back to looking like a poster,
  // not shout an error over the homepage. The failure is logged by the hook itself.
  useHlsPlayback(heroVideoRef, heroPlaybackUrl, currentLiveVideo?.delivery_format);

  // This element used to carry isMuted in its `key`, which remounted the whole <video>
  // to change one boolean - restarting playback from zero on every mute toggle, and with
  // hls.js attached it would tear down and rebuild the stream as well. The key is now the
  // video id alone. This effect keeps the DOM property in step as a belt-and-braces
  // measure, since `muted` is the one media attribute React has historically been
  // unreliable about reflecting.
  useEffect(() => {
    if (heroVideoRef.current) heroVideoRef.current.muted = isMuted;
  }, [isMuted, heroPlaybackUrl]);
  
  /**
   * Clear what the removed viewer, like and comment counters left in storage.
   *
   * Those three keys were written on every homepage visit and read by nothing. Dropping
   * the code that wrote them does not remove what it already wrote, so anybody who
   * visited before this change keeps the values in their browser indefinitely. Harmless,
   * but it is residue from a feature that no longer exists, and clearing it is cheaper
   * than explaining it later.
   */
  useEffect(() => {
    try {
      ['hero_viewer_count', 'hero_like_count', 'hero_comment_count'].forEach((key) =>
        localStorage.removeItem(key),
      );
    } catch {
      // Private mode or blocked site data. Nothing reads these keys, so failing to
      // clear them costs nothing worth a thrown error on the homepage.
    }
  }, []);

  // Check authentication status using unified auth system
  useEffect(() => {
    const checkAuth = () => {
      // Use the same authentication system as admin services
      const isLoggedIn = unifiedAuth.isAuthenticated();
      setIsAuthenticated(isLoggedIn);
    };
    
    // Check initially
    checkAuth();
    
    // Listen for storage changes (login/logout events) - both localStorage and sessionStorage
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'access_token' || event.key === '_app_secure_session') {
        checkAuth();
      }
    };
    
    // Listen for custom login/logout events
    const handleAuthChange = () => {
      checkAuth();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authChanged', handleAuthChange);
    
    // Check periodically for session changes
    const authCheckInterval = setInterval(checkAuth, 2000); // Reduced frequency
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authChanged', handleAuthChange);
      clearInterval(authCheckInterval);
    };
  }, []);

  // Load platform stats
  useEffect(() => {
    const loadPlatformStats = async () => {
      try {
        setStatsLoading(true);
        
        // Fetch data from all services in parallel
        const [originalsStatsResponse, podcastStatsResponse, cinemaResponse, contentResponse] = await Promise.allSettled([
          originalsService.getOriginals({ page: 1, size: 1 }),
          podcastService.getStats(),
          mediaService.getCinema({ page_size: 1 }), // Just need the count
          mediaService.getContent({ page_size: 1 }) // Just need the count
        ]);

        let originalsCount = 0;
        let podcastsCount = 0;
        let cinemaCount = 0;
        let contentCount = 0;

        // Extract originals count
        if (originalsStatsResponse.status === 'fulfilled') {
          originalsCount = originalsStatsResponse.value.data?.totalElements || 0;
        }

        // Extract podcasts count  
        if (podcastStatsResponse.status === 'fulfilled') {
          podcastsCount = podcastStatsResponse.value.totalPodcasts || 0;
        }

        // Extract cinema count
        if (cinemaResponse.status === 'fulfilled') {
          cinemaCount = cinemaResponse.value.count || 0;
        }

        // Extract content count
        if (contentResponse.status === 'fulfilled') {
          contentCount = contentResponse.value.count || 0;
        }

        // Combine cinema + content for "Contents"
        const totalContents = cinemaCount + contentCount;

        setStats({
          originals: originalsCount,
          contents: totalContents,
          podcasts: podcastsCount,
        });

      } catch (_error) {
        console.warn('Error loading platform stats:', _error);
        // Keep default values on error
      } finally {
        setStatsLoading(false);
      }
    };

    loadPlatformStats();
  }, []);
  
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Record view function for live video
  const recordView = useCallback(async () => {
    if (hasRecordedView || !currentLiveVideo || !isAuthenticated) return null;
    
    try {
      const response = await mediaService.recordView(currentLiveVideo.id);
      if (response.viewId) {
        setHasRecordedView(true);
      }
      return response.viewId;
    } catch (error) {
      console.error('Failed to record view for live video:', error);
      return null;
    }
  }, [currentLiveVideo, hasRecordedView, isAuthenticated]);

  // Separate effect for fetching live video with auto-refresh
  useEffect(() => {
    const fetchCurrentLiveVideo = async () => {
      try {
        setHasTriedFetch(true);
        const liveVideo = await liveVideoService.getCurrentLiveVideo();
        if (liveVideo) {
          
          setCurrentLiveVideo(liveVideo);
        }
      } catch (_error) {
        // Backend endpoints not implemented yet - silently handle
        setCurrentLiveVideo(null);
        console.info('Live video feature not available: Backend endpoints need to be implemented');
      }
    };

    if (!hasTriedFetch) {
      fetchCurrentLiveVideo();
    }

    // Set up auto-refresh every 5 minutes (reduced frequency to prevent interruptions)
    const interval = setInterval(fetchCurrentLiveVideo, 300000); // 5 minutes instead of 30 seconds
    
    // Disable URL refresh interval for now to prevent interruptions
    // const urlRefreshInterval = setInterval(() => {
    //   if (currentLiveVideo) {
    //     // Refreshing video URL to prevent token expiration...
    //     fetchCurrentLiveVideo();
    //   }
    // }, 600000); // 10 minutes
    
    return () => {
      clearInterval(interval);
      // clearInterval(urlRefreshInterval);
    };
  }, [hasTriedFetch]);

  // Effect to record view when video starts playing
  useEffect(() => {
    let recordViewTimeout: number;
    
    if (isVideoPlaying && currentLiveVideo && !hasRecordedView && isAuthenticated) {
      // Record view after 3 seconds of playback
      recordViewTimeout = window.setTimeout(() => {
        recordView();
      }, 3000);
    }
    
    return () => {
      if (recordViewTimeout) {
        window.clearTimeout(recordViewTimeout);
      }
    };
  }, [isVideoPlaying, currentLiveVideo, hasRecordedView, isAuthenticated, recordView]);

  // Simplified toggle mute function
  const toggleMute = useCallback(() => {
    setIsMuted(!isMuted);
  }, [isMuted]);

  // Search functionality
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to originals page with search query
      navigate(`/originals?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  // Quick navigation functions
  const navigateToContent = (type: string) => {
    switch (type) {
      case 'originals':
        navigate('/originals');
        break;
      case 'cinema':
        navigate('/media/cinema');
        break;
      case 'contents':
        navigate('/media/contents');
        break;
      case 'media':
        navigate('/media');
        break;
      case 'podcasts':
        navigate('/podcasts');
        break;
      case 'animations':
        navigate('/animations');
        break;
      case 'sneak-peeks':
        navigate('/sneak-peeks');
        break;
      default:
        navigate('/');
    }
  };

  return (
    <>
      {/* SEO Meta Tags for current live video */}
      {currentLiveVideo && (
        <VideoSEO
          title={`${currentLiveVideo.title || 'Live Video'} - Watch Live on StrymHub`}
          description="Watch live videos, connect with creators, and discover amazing content on StrymHub."
          videoUrl={currentLiveVideo.secure_video_url || currentLiveVideo.video_file || ''}
          thumbnailUrl={currentLiveVideo.secure_thumbnail_url || 'https://strymhub.com/android-chrome-512x512.png'}
          publishedTime={new Date().toISOString()}
          author="StrymHub Creator"
          canonicalUrl={pageUrl}
          embedUrl={`https://strymhub.com/live/${currentLiveVideo.slug}/embed`}
        />
      )}
      
      <section className="relative min-h-screen bg-gradient-to-br from-slate-950 via-neutral-900 to-slate-950 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-red-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
          
          {/* Text Content */}
          <div className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
            
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 backdrop-blur-sm mb-6">
              <Sparkles className="h-4 w-4 text-orange-400 mr-2" />
              <span className="text-sm text-orange-300 font-medium">New Contents Every Day</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6">
              <span className="bg-gradient-to-r from-white via-orange-200 to-pink-200 bg-clip-text text-transparent">
                Where originals
              </span>
              <br />
              <span className="font-display bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
                Come Alive
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-lg">
              Immerse yourself in a world of captivating originals, breathtaking cinema, 
              engaging podcasts, and stunning animations. Your journey into extraordinary 
              content starts here.
            </p>

            {/* Stats */}
            <div className="flex flex-wrap gap-8 mb-10">
              <div className="text-center">
                <div className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                  {statsLoading ? '...' : formatCount(stats.originals)}
                </div>
                <div className="text-sm text-gray-400">Originals</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                  {statsLoading ? '...' : formatCount(stats.contents)}
                </div>
                <div className="text-sm text-gray-400">Contents</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold bg-gradient-to-r from-amber-400 to-cyan-400 bg-clip-text text-transparent">
                  {statsLoading ? '...' : formatCount(stats.podcasts)}
                </div>
                <div className="text-sm text-gray-400">Podcasts</div>
              </div>
            </div>

            {/* Search and Navigation */}
            <div className="space-y-4">
              {/* Search Bar */}
              <form onSubmit={handleSearch} className="relative">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search contents, podcasts, cinema..."
                    className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-12 py-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:bg-white/20 transition-all duration-300"
                  />
                  <button
                    type="submit"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-700 hover:to-red-700 text-white px-6 py-2 rounded-full font-medium transition-all duration-300 hover:scale-105"
                  >
                    Search
                  </button>
                </div>
              </form>

              {/* Quick Navigation Buttons */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <button
                  onClick={() => navigateToContent('originals')}
                  className="group bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 text-white px-4 py-3 rounded-xl font-medium transition-all duration-300 hover:scale-105 flex items-center justify-center text-sm"
                >
                  <Clapperboard className="mr-2 h-4 w-4 group-hover:scale-110 transition-transform duration-200" />
                  Originals
                </button>
                
                <button
                  onClick={() => navigateToContent('cinema')}
                  className="group bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 text-white px-4 py-3 rounded-xl font-medium transition-all duration-300 hover:scale-105 flex items-center justify-center text-sm"
                >
                  <Film className="mr-2 h-4 w-4 group-hover:scale-110 transition-transform duration-200" />
                  Cinema
                </button>
                
                <button
                  onClick={() => navigateToContent('podcasts')}
                  className="group bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 text-white px-4 py-3 rounded-xl font-medium transition-all duration-300 hover:scale-105 flex items-center justify-center text-sm"
                >
                  <Headphones className="mr-2 h-4 w-4 group-hover:scale-110 transition-transform duration-200" />
                  Podcasts
                </button>
                
                <button
                  onClick={() => navigateToContent('animations')}
                  className="group bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 text-white px-4 py-3 rounded-xl font-medium transition-all duration-300 hover:scale-105 flex items-center justify-center text-sm"
                >
                  <Zap className="mr-2 h-4 w-4 group-hover:scale-110 transition-transform duration-200" />
                  Animations
                </button>
              </div>
            </div>
          </div>

          {/* Enhanced Live Video Player - Restored Original Design */}
          <div className={`transform transition-all duration-1000 delay-300 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
            <div className="relative">
              
              {/* Main Video Container - Back to Original 4:5 Aspect */}
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
                <div className="aspect-[4/5] bg-gradient-to-br from-slate-800 to-slate-900 relative group">
                  
                  {/* Video Element or Gradient Overlay */}
                  {(() => {
                    if (currentLiveVideo) {
                      return (
                        <>
                          <div 
                            className="absolute inset-0 w-full h-full cursor-pointer"
                            onClick={() => navigate(`/live/${currentLiveVideo.slug}`)}
                          >
                            <video
                              ref={heroVideoRef}
                              key={currentLiveVideo?.id}
                              className="absolute inset-0 w-full h-full object-cover"
                              autoPlay={true}
                              loop={true}
                              controls={false}
                              muted={isMuted}
                              playsInline
                              onPlay={() => {
                                // Video started playing - track for view recording
                                if (!isVideoPlaying) {
                                  setIsVideoPlaying(true);
                                }
                              }}
                              onTimeUpdate={(e) => {
                                // Track playback time for view progress
                                const currentTime = (e.target as HTMLVideoElement).currentTime;
                                if (currentTime > 0 && !isVideoPlaying) {
                                  setIsVideoPlaying(true);
                                }
                              }}
                            >
                              Your browser does not support the video tag.
                            </video>
                          </div>
                      
                      {/* Sparkles Element - Top Right */}
                      <div className="absolute top-4 right-4 z-20 bg-white/10 backdrop-blur-md rounded-full p-3">
                        <Sparkles className="h-6 w-6 text-orange-400 animate-pulse" />
                      </div>

                      {/* Mute toggle - Bottom Right.
                          The only control over the hero video. A viewer count, a like
                          button and a comment button with a chat panel used to sit here
                          too; they were removed as furniture the homepage did not need. */}
                      <div
                        className="absolute bottom-4 right-4 z-20 flex space-x-2"
                        data-testid="hero-live-controls"
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleMute();
                          }}
                          aria-label={isMuted ? 'Unmute video' : 'Mute video'}
                          className="p-2 bg-black/50 backdrop-blur-md rounded-full hover:bg-black/70 transition-colors"
                        >
                          {isMuted ? (
                            <VolumeX className="h-4 w-4 text-white" />
                          ) : (
                            <Volume2 className="h-4 w-4 text-white" />
                          )}
                        </button>
                      </div>
                    </>
                  );
                } else {
                  return (
                    <div className="absolute inset-0 bg-gradient-to-t from-orange-900/80 via-transparent to-red-900/20">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center p-8">
                          <div className="w-24 h-24 bg-gradient-to-r from-orange-500 to-red-500 rounded-full mx-auto mb-6 flex items-center justify-center">
                            <Play className="h-12 w-12 text-white" />
                          </div>
                          <h3 className="text-2xl font-bold text-white mb-2">No Videos Yet</h3>
                          <p className="text-gray-300 mb-6">Videos will appear here when uploaded by admin</p>
                        </div>
                      </div>
                    </div>
                  );
                }
              })()}

                  {/* Bottom Status */}
                  <div className="absolute bottom-4 left-4 bg-gradient-to-r from-orange-500/20 to-red-500/20 backdrop-blur-md rounded-2xl p-4 border border-white/10">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-sm text-white font-medium">Contents Available</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Original Decorative Elements */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-r from-cyan-400/20 to-orange-400/20 rounded-full blur-xl animate-pulse z-0"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-r from-amber-400/20 to-orange-400/20 rounded-full blur-xl animate-pulse delay-1000 z-0"></div>
              
              {/* Original Floating Cards */}
              <div className="absolute -left-12 top-1/3 z-20 bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/10 transform rotate-6 hover:rotate-3 transition-transform duration-300 hidden lg:block">
                <div className="w-16 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg mb-2"></div>
                <div className="text-xs text-white font-medium">Originals</div>
                <div className="text-xs text-gray-400">
                  {statsLoading ? 'Loading...' : `${formatCount(stats.originals)} Available`}
                </div>
              </div>
              
              <div className="absolute -right-12 top-2/3 z-20 bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/10 transform -rotate-6 hover:-rotate-3 transition-transform duration-300 hidden lg:block">
                <div className="w-16 h-12 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg mb-2"></div>
                <div className="text-xs text-white font-medium">Contents</div>
                <div className="text-xs text-gray-400">
                  {statsLoading ? 'Loading...' : `${formatCount(stats.contents)} Available`}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Responsive Scroll Indicator */}
      <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-white/30 rounded-full flex justify-center">
          <div className="w-0.5 sm:w-1 h-2 sm:h-3 bg-gradient-to-b from-orange-500 to-red-500 rounded-full mt-1.5 sm:mt-2 animate-pulse"></div>
        </div>
      </div>

    </section>
    </>
  );
};

export default Hero;